/**
 * Sprzeciw od kary porządkowej z portalu (art. 112 § 1 k.p.; fala 8, grupa 43).
 *
 * Sprzeciw wnosi pracownik w ciągu siedmiu dni od doręczenia zawiadomienia.
 * Przepis nie żąda uzasadnienia, więc pole jest dobrowolne — ale to jedyne
 * miejsce, w którym pracownik mówi pracodawcy, dlaczego kara narusza prawo,
 * i dlatego zostaje przy karze obok uzasadnienia decyzji. Błąd ma własny
 * komunikat w oknie, a nie ogólny baner pod listą wniosków.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, FormField, InfoBanner, Modal, Spinner, useToast } from '@k1p4/ui'
import { formatDate } from '@k1p4/i18n'
import { ApiError, api, type PenaltyItem } from '../../api/client'
import styles from '../app/ChangeRequestModal.module.css'

export interface PenaltyObjectionModalProps {
  /** Kara, od której pracownik wnosi sprzeciw; `null` zamyka okno. */
  penalty: PenaltyItem | null
  onClose: () => void
}

/** Kody odmowy z trasy sprzeciwu, które mają własny komunikat. */
const KNOWN_ERRORS = ['NOT_NOTIFIED', 'ALREADY_OBJECTED', 'ALREADY_DECIDED', 'AFTER_DEADLINE']

export function PenaltyObjectionModal({ penalty, onClose }: PenaltyObjectionModalProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const commentId = useId()
  const [comment, setComment] = useState('')
  const [error, setError] = useState<string | null>(null)

  function close(): void {
    setComment('')
    setError(null)
    onClose()
  }

  const objection = useMutation({
    mutationFn: () => api.objectToPenalty(penalty!.id, comment.trim() || undefined),
    onSuccess: (result) => {
      toast.show(
        t('penalties.objectionFiled', { date: formatDate(result.objectionSilenceOn, 'dot') }),
        { tone: 'success' },
      )
      void queryClient.invalidateQueries({ queryKey: ['my-penalties'] })
      close()
    },
    onError: (caught) => {
      setError(
        caught instanceof ApiError && KNOWN_ERRORS.includes(caught.code)
          ? `penalties.objectionErrors.${caught.code}`
          : 'penalties.objectionError',
      )
    },
  })

  return (
    <Modal open={penalty !== null} onClose={close} title={t('penalties.objectionTitle')}>
      <p className={styles.subtitle}>
        {penalty?.objectionDeadline
          ? t('penalties.objectionLead', { date: formatDate(penalty.objectionDeadline, 'dot') })
          : null}
      </p>
      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}
      <FormField
        label={t('penalties.fields.objectionReason')}
        htmlFor={commentId}
        hint={t('penalties.fields.objectionReasonHint')}
      >
        <textarea
          id={commentId}
          className={styles.textarea}
          rows={4}
          maxLength={2000}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />
      </FormField>
      <div className={styles.actions}>
        <Button variant="secondary" onClick={close}>
          {t('penalties.cancel')}
        </Button>
        <Button disabled={objection.isPending} onClick={() => objection.mutate()}>
          {objection.isPending ? <Spinner label={t('penalties.saving')} /> : t('penalties.object')}
        </Button>
      </div>
    </Modal>
  )
}
